/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {environment} from '../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {Observable} from 'rxjs';
import {UserResultModel} from '../contracts/user-result.model';
import {UserBaseModel} from '../contracts/user-base.model';
import {UserInformationContract} from '../contracts/user-information.contract';
import {GroupTypeEnum} from '../contracts/group-type.enum';
import {UserQueryContract} from '../contracts/user-query.contract';

@Injectable({
    providedIn: 'root'
})
export class UserManagementService {

    private userManagementBaseUrl = `${environment.securityBaseUrl}/api/user`;

    constructor(private http: HttpClient) {
    }

    /**
     * Gets the users with roles from the BE
     * @param result userQueryModel with search criteria and pagination criteria
     * **/
    getUsersWithRoles(userQueryResult: UserQueryContract):
            Observable<UserResultModel> {
        return this.http.post<UserResultModel>(`${this.userManagementBaseUrl}/search`, userQueryResult);
    }

    /**
     * Grants a role to an tableUser
     * @param username The username of the tableUser to grant the role
     * @param role The code of the role to grant to the tableUser
     * @param expiresOn The role expiration date
     * @param context The user context 'PNW2/ASCC'
     * **/
    grantRole(username: string, role: string, expiresOn: number, context: string): Observable<void> {
        return this.http.put<void>(`${this.userManagementBaseUrl}/${username}/context/${context}/role/${role}`, {
            expiresOn: expiresOn
        });
    }

    /**
     * Revokes a role from an tableUser
     * @param username The username of the tableUser to revoke the role
     * @param role The code of the role to be revoked from the tableUser
     * @param context The user context 'PNW2/ASCC'
     * **/
    revokeRole(username: string, role: string, context: string): Observable<void> {
        return this.http.delete<void>(`${this.userManagementBaseUrl}/${username}/context/${context}/role/${role}`);
    }

    /**
     * Grants a entity group to an tableUser
     * @param username The username of the tableUser to grant
     * @param groupType The type of the entity group
     * @param entityGroupCode The code of the entity group to grant
     * @param context The user context 'PNW2/ASCC'
     **/
    grantEntityGroup(username: string, groupType: GroupTypeEnum, entityGroupCode: string, context: string): Observable<void> {
        return this.http.put<void>(`${this.userManagementBaseUrl}/${username}/context/${context}/groupType/${groupType}/${entityGroupCode}`,
                {headers: new HttpHeaders({'Content-Type': 'application/json'})});
    }

    /**
     * Revokes a entity group from an tableUser
     * @param username The username of the tableUser to revoke the entityGroup
     * @param groupType The type of the entity group
     * @param entityGroupCode The code of the entity element to be revoked from the tableUser
     * @param context The user context 'PNW2/ASCC'
     * **/
    revokeEntityGroup(username: string, groupType: GroupTypeEnum, entityGroupCode: string, context: string): Observable<void> {
        return this.http.delete<void>(
                `${this.userManagementBaseUrl}/${username}/context/${context}/groupType/${groupType}/${entityGroupCode}`);
    }

    getAutocompleteUserWithNoRolesList(query: string, context: string, firstResult: number = 0, maxResults: number = 20):
            Observable<UserBaseModel[]> {
        return this.http.get<UserBaseModel[]>(`${this.userManagementBaseUrl}/${context}/untreated?` + [
            `identification=${query}`,
            `firstResult=${firstResult}`,
            `maxResults=${maxResults}`
        ].join('&'));
    }

    reloadUser(): Observable<UserInformationContract> {
        return this.http.get<UserInformationContract>(`${this.userManagementBaseUrl}/reload`);
    }
}
